// Blokada aplikacji ze sklepu PIN-em (docs/pwa/NATYWNA.md 4 „Blokada aplikacji”, właściciel 29.10): domyślnie wyłączona,
// klient włącza w Ustawieniach. PIN 4-6 cyfr (długość wybiera sam), opcjonalnie odcisk palca / twarz (PIN zawsze
// zapasowo). Prosi przy uruchomieniu i po powrocie z tła po czasie, który wybrał (od razu / 1 / 5 / 30 min). PIN tylko
// w telefonie: skrót PBKDF2 z solą w Keychain / Keystore (SecureStorage), nie na serwerze. 5 złych PIN-ów albo
// „Nie pamiętam PIN-u” = wylogowanie i skasowanie PIN-u (odzyskanie = zwykłe logowanie i nowy PIN).
import { useSyncExternalStore } from "react";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import { BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { natywna } from "./instalacja";

export const CZASY_BLOKADY = [0, 1, 5, 30] as const;
export type CzasBlokady = (typeof CZASY_BLOKADY)[number];

/** Ustawienia blokady widoczne w Ustawieniach (bez skrótu PIN-u). */
export interface OpcjeBlokady {
  biometria: boolean;
  poMin: CzasBlokady;
}

interface Zapis extends OpcjeBlokady {
  sol: string;
  skrot: string;
  bledy: number;
}

const KLUCZ = "blokada";
const ITERACJE = 150_000;
export const MAX_BLEDOW = 5;

let zapis: Zapis | null = null;
let zablokowana = false;
let gotowa = !natywna();
let wTle = 0;
let odciskTrwa = false;
const sluchacze = new Set<() => void>();
const powiadom = () => { for (const f of sluchacze) f(); };

const b64 = (b: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(b)));
const zB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function skrot(pin: string, sol: Uint8Array): Promise<string> {
  const k = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, ["deriveBits"]);
  return b64(await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: sol as BufferSource, iterations: ITERACJE }, k, 256));
}

async function zapisz(z: Zapis | null): Promise<void> {
  zapis = z;
  if (z) await SecureStorage.setItem(KLUCZ, JSON.stringify(z));
  else await SecureStorage.removeItem(KLUCZ);
}

if (natywna()) {
  void SecureStorage.getItem(KLUCZ).then((v) => { zapis = v ? (JSON.parse(v) as Zapis) : null; }, () => undefined).finally(() => {
    zablokowana = zapis !== null; // przy uruchomieniu zawsze
    gotowa = true;
    powiadom();
  });
  document.addEventListener("visibilitychange", () => {
    if (!zapis || odciskTrwa) return; // okno odcisku palca na części telefonów chowa stronę - to nie wyjście z aplikacji
    if (document.visibilityState === "hidden") wTle = Date.now();
    else if (wTle && Date.now() - wTle >= zapis.poMin * 60_000) {
      zablokowana = true;
      powiadom();
    }
  });
}

/** Stan blokady dla powłoki: `sprawdzam` (ustawienia jeszcze się czytają), `zablokowana`, `otwarta`. */
export function useBlokada(): "sprawdzam" | "zablokowana" | "otwarta" {
  return useSyncExternalStore((f) => { sluchacze.add(f); return () => sluchacze.delete(f); },
    () => (!gotowa ? "sprawdzam" : zablokowana ? "zablokowana" : "otwarta"));
}

/** Opcje włączonej blokady; null - wyłączona. */
export const opcjeBlokady = (): OpcjeBlokady | null => (zapis ? { biometria: zapis.biometria, poMin: zapis.poMin } : null);

/** Ustawienie (albo zmiana) PIN-u z opcjami - włącza blokadę. */
export async function ustawPin(pin: string, o: OpcjeBlokady): Promise<void> {
  const sol = crypto.getRandomValues(new Uint8Array(16));
  await zapisz({ ...o, sol: b64(sol), skrot: await skrot(pin, sol), bledy: 0 });
  powiadom();
}

export async function zmienOpcje(o: OpcjeBlokady): Promise<void> {
  if (zapis) await zapisz({ ...zapis, ...o });
  powiadom();
}

/** Wyłączenie blokady (Ustawienia) albo wylogowanie - PIN znika z telefonu. */
export async function wylaczBlokade(): Promise<void> {
  zablokowana = false;
  await zapisz(null).catch(() => undefined);
  powiadom();
}

/** Sprawdzenie PIN-u: `ok` - odblokowana; `zly` - zostało prób; `koniec` - 5 złych (do wylogowania). */
export async function sprawdzPin(pin: string): Promise<{ wynik: "ok" | "zly" | "koniec"; zostalo: number }> {
  if (!zapis) return { wynik: "ok", zostalo: MAX_BLEDOW };
  if ((await skrot(pin, zB64(zapis.sol))) === zapis.skrot) {
    await zapisz({ ...zapis, bledy: 0 });
    zablokowana = false;
    powiadom();
    return { wynik: "ok", zostalo: MAX_BLEDOW };
  }
  const bledy = zapis.bledy + 1;
  await zapisz({ ...zapis, bledy });
  return { wynik: bledy >= MAX_BLEDOW ? "koniec" : "zly", zostalo: MAX_BLEDOW - bledy };
}

/** Jest czytnik odcisku / twarzy z zapisaną biometrią. */
export const biometriaDostepna = () => BiometricAuth.checkBiometry().then((r) => r.isAvailable, () => false);

/** Odcisk palca / twarz; true - człowiek potwierdzony (przy blokadzie - odblokowuje). */
export async function biometria(powod: string, anuluj: string): Promise<boolean> {
  odciskTrwa = true;
  try {
    await BiometricAuth.authenticate({ reason: powod, androidTitle: powod, cancelTitle: anuluj, allowDeviceCredential: false });
  } catch {
    return false;
  } finally {
    setTimeout(() => { odciskTrwa = false; wTle = 0; }, 1000); // powrót strony po zamknięciu okna odcisku
  }
  if (zapis) {
    await zapisz({ ...zapis, bledy: 0 });
    zablokowana = false;
    powiadom();
  }
  return true;
}
